/**
 * The one place the SPA talks to the API.
 *
 * In production the browser attaches an Entra ID bearer token and the API
 * reaches SQL with its own credentials — there is no direct client-to-database
 * path at any point. In development the API runs with Auth:DevMode and accepts
 * an X-Dev-User header instead, which is what the role switcher sets.
 */

const DEV_USER_KEY = 'cyntox.devUser';

/** Raised for a 409 so callers can show the conflict banner instead of an error. */
export class ConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConflictError';
  }
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getDevUser(): string | null {
  return localStorage.getItem(DEV_USER_KEY);
}

export function setDevUser(upn: string | null) {
  if (upn) localStorage.setItem(DEV_USER_KEY, upn);
  else localStorage.removeItem(DEV_USER_KEY);
}

/** Set by the Teams/MSAL bootstrap once real auth is wired in. */
let bearerTokenProvider: (() => Promise<string | null>) | null = null;
export function setTokenProvider(fn: () => Promise<string | null>) {
  bearerTokenProvider = fn;
}

async function headers(extra?: HeadersInit): Promise<Headers> {
  const h = new Headers(extra);
  const token = bearerTokenProvider ? await bearerTokenProvider() : null;
  if (token) h.set('Authorization', `Bearer ${token}`);
  const devUser = getDevUser();
  if (devUser) h.set('X-Dev-User', devUser);
  return h;
}

async function handle<T>(res: Response): Promise<T> {
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : (undefined as T);
  }

  let message = res.statusText;
  try {
    const body = await res.json();
    if (body?.message) message = body.message;
  } catch {
    /* non-JSON error body — keep the status text */
  }

  if (res.status === 409) throw new ConflictError(message);
  throw new ApiError(message, res.status);
}

function url(path: string, query?: Record<string, unknown>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '' || v === 'all') continue;
    // A multi-value filter sends the key once per value — ?location=A&location=B
    // — which is what ASP.NET binds to a string[] parameter without help. An
    // empty array means "no filter", the same thing 'all' means for one value,
    // so it is left off entirely rather than sent as an empty string.
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item === undefined || item === null || item === '') continue;
        params.append(k, String(item));
      }
      continue;
    }
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export const api = {
  async get<T>(path: string, query?: Record<string, unknown>): Promise<T> {
    return handle<T>(await fetch(url(path, query), { headers: await headers() }));
  },

  async post<T>(path: string, body?: unknown): Promise<T> {
    return handle<T>(
      await fetch(path, {
        method: 'POST',
        headers: await headers({ 'Content-Type': 'application/json' }),
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    );
  },

  async put<T>(path: string, body?: unknown): Promise<T> {
    return handle<T>(
      await fetch(path, {
        method: 'PUT',
        headers: await headers({ 'Content-Type': 'application/json' }),
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    );
  },

  async del<T>(path: string): Promise<T> {
    return handle<T>(await fetch(path, { method: 'DELETE', headers: await headers() }));
  },

  /**
   * Saves a file the API generates. It goes through fetch rather than a plain
   * link because the identity lives in a header — the dev-user header now, the
   * bearer token in production — and an <a href> cannot carry either.
   */
  async download(path: string, query?: Record<string, unknown>): Promise<void> {
    const res = await fetch(url(path, query), { headers: await headers() });
    if (!res.ok) return handle(res);

    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const name = match ? decodeURIComponent(match[1]) : 'download.csv';

    const href = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  },

  /**
   * A file the API serves, as a Blob for the page to show — a photo
   * thumbnail, say. Fetched rather than linked for the same reason as
   * download: the identity travels in a header an <img src> cannot carry.
   */
  async blob(path: string): Promise<Blob> {
    const res = await fetch(path, { headers: await headers() });
    if (!res.ok) return handle(res);
    return res.blob();
  },

  /** Sends a file, and any text fields that go with it, as one form. */
  async upload<T>(path: string, file: File, fields?: Record<string, string>): Promise<T> {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields ?? {})) form.append(k, v);
    form.append('file', file);
    return handle<T>(await fetch(path, { method: 'POST', headers: await headers(), body: form }));
  },
};
